
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText, Lightbulb, BarChart, MessageSquare, Clock, Star, Users, ThumbsUp, MousePointerClick } from "lucide-react";

interface TemplateItemProps {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  estimated_time?: string;
  popularity?: {
    likes?: number;
    uses?: number;
  };
  isSelected?: boolean;
  onSelect: (id: string) => void;
  displayType?: "card" | "sidebar";
  tags?: string[];
}

const TemplateItem = ({
  id,
  title,
  description,
  icon,
  category,
  estimated_time,
  popularity,
  isSelected = false,
  onSelect,
  displayType = "sidebar",
  tags = []
}: TemplateItemProps) => {
  
  const getIconComponent = (iconType: string) => {
    switch(iconType) {
      case 'prd':
        return <FileText className="h-6 w-6 text-blue-500" />;
      case 'brainstorm':
        return <Lightbulb className="h-6 w-6 text-amber-500" />;
      case 'research':
        return <BarChart className="h-6 w-6 text-orange-500" />;
      case 'voc':
        return <MessageSquare className="h-6 w-6 text-green-500" />;
      default:
        return <FileText className="h-6 w-6 text-gray-500" />;
    }
  };
  
  if (displayType === "card") {
    return (
      <Card 
        className={`h-full hover:shadow-md transition-shadow cursor-pointer border ${
          isSelected ? 'border-primary/50 bg-primary/5' : 'border-border/40'
        }`}
        onClick={() => onSelect(id)}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-primary/5 rounded-lg">
              {getIconComponent(icon)}
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg line-clamp-1">{title}</CardTitle>
              <div className="flex gap-1 flex-wrap mt-1">
                <Badge variant="outline" className="text-xs">
                  {category}
                </Badge>
                {tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pb-2">
          <CardDescription className="line-clamp-3 text-sm">
            {description}
          </CardDescription>
        </CardContent>
        <CardFooter className="pt-2 flex justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{estimated_time || "10-15 min"}</span>
          </div>
          {popularity && (
            <div className="flex gap-2">
              {popularity.likes !== undefined && (
                <div className="flex items-center gap-1">
                  <ThumbsUp className="h-3 w-3" />
                  <span>{popularity.likes}</span>
                </div>
              )}
              {popularity.uses !== undefined && (
                <div className="flex items-center gap-1">
                  <MousePointerClick className="h-3 w-3" />
                  <span>{popularity.uses}</span>
                </div>
              )}
            </div>
          )}
        </CardFooter>
      </Card>
    );
  }
  
  return (
    <div 
      className={`flex items-start space-x-3 p-2 rounded-lg cursor-pointer transition-colors ${
        isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'
      }`}
      onClick={() => onSelect(id)}
    >
      {getIconComponent(icon)}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm line-clamp-1">{title}</h4>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{description}</p>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          <span>{category}</span>
          <span>•</span>
          <div className="flex items-center">
            <Clock className="h-3 w-3 mr-1" />
            <span>{estimated_time || "10-15 min"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateItem;
